import _ from 'lodash';
import FileState from '../enums/FileState';
import {
  WHEEL_INSURANCE_VALUES,
  WHEEL_PHOTO_PART,
  WHEEL_POSITIONS,
  WHEEL_PRE_PHOTOS_PER_POSITION,
  WHEEL_LOCATION_MANUAL_ONLY_KEYS,
  prePhotoPart,
} from '../constants/WheelInspectionConstants';
import {
  mergeWheelIntoVehicleInformation,
  parseWheelInformation,
  hasWheelInformation,
} from './wheelInformationUtil';

/** 복원후 위치별 상세점검 및 측정(안전검사) 기본값 */
export const DEFAULT_LOCATION_DETAIL = {
  crack: 'GOOD',
  bending: 'NONE',
  balance: 'GOOD',
  paint: 'GOOD',
  clearCoat: 'GOOD',
  cuttingPart: 'GOOD',
  alignment: '',
};

function createDefaultLocationDetail() {
  return { ...DEFAULT_LOCATION_DETAIL };
}

function normalizeLocationDetail(raw = {}) {
  const merged = { ...createDefaultLocationDetail(), ...raw };
  if (merged.crack === 'NONE') {
    merged.crack = 'GOOD';
  }
  if (merged.bending === 'GOOD') {
    merged.bending = 'NONE';
  }
  if (merged.bending === 'BAD') {
    merged.bending = 'YES';
  }
  Object.keys(DEFAULT_LOCATION_DETAIL).forEach((key) => {
    if (WHEEL_LOCATION_MANUAL_ONLY_KEYS.includes(key)) return;
    if (!merged[key]) {
      merged[key] = DEFAULT_LOCATION_DETAIL[key];
    }
  });
  return merged;
}

function normalizeInsuranceHandled(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((value) => WHEEL_INSURANCE_VALUES.includes(value));
  }
  if (typeof raw === 'string' && WHEEL_INSURANCE_VALUES.includes(raw)) {
    return [raw];
  }
  return [];
}

/** §5↔§6 컷팅 부분·노면력 필드 위치 교환 — 기존 데이터 호환 */
function migrateCuttingPartAndRoadForce(parsed) {
  const safety = parsed.safety || {};

  if (safety.cuttingPart || safety.cuttingPartPositions?.length) {
    const positions = safety.cuttingPartPositions?.length
      ? safety.cuttingPartPositions
      : WHEEL_POSITIONS.map(({ code }) => code);
    if (safety.cuttingPart) {
      positions.forEach((code) => {
        if (parsed.locationDetail?.[code]) {
          parsed.locationDetail[code].cuttingPart = safety.cuttingPart;
        }
      });
    }
    delete safety.cuttingPart;
    delete safety.cuttingPartPositions;
  }

  const roadForcePositions = [];
  let roadForceValue = '';
  WHEEL_POSITIONS.forEach(({ code }) => {
    const row = parsed.locationDetail?.[code];
    if (!row || !Object.prototype.hasOwnProperty.call(row, 'roadForce')) return;
    const value = row.roadForce;
    if (!safety.roadForce && value && value !== 'GOOD') {
      roadForcePositions.push(code);
      if (!roadForceValue) roadForceValue = value;
    }
    delete row.roadForce;
  });
  if (!safety.roadForce && roadForceValue) {
    safety.roadForce = roadForceValue;
    safety.roadForcePositions = roadForcePositions;
  }
}

export const DEFAULT_WHEEL_INSPECTION = {
  preRestoration: {
    insuranceHandled: [],
    positions: [],
  },
  safety: {
    wheelTypes: [],
    restorationMethods: [],
    clearCoatType: '',
    precisionMethods: [],
    crack: '',
    crackPositions: [],
    hairline: '',
    hairlinePositions: [],
    paintState: '',
    paintStatePositions: [],
    roadForce: '',
    roadForcePositions: [],
    scratch: '',
    scratchPositions: [],
    specialNote: '',
  },
  locationDetail: {
    FL: createDefaultLocationDetail(),
    FR: createDefaultLocationDetail(),
    RL: createDefaultLocationDetail(),
    RR: createDefaultLocationDetail(),
  },
};

const inspectionPath = 'vehicleInformation.wheelInspection';

/** §5 복원전 검사항목 — 항목별 위치 필드 */
export const SAFETY_INSPECTION_POSITION_PATHS = [
  'safety.crackPositions',
  'safety.hairlinePositions',
  'safety.paintStatePositions',
  'safety.roadForcePositions',
  'safety.scratchPositions',
];

const sortWheelPositionCodes = (codes = []) =>
  WHEEL_POSITIONS.map(({ code }) => code).filter((code) => codes.includes(code));

/**
 * 복원 전 위치(preRestoration.positions) 변경 시 검사항목 위치를 동기화.
 * - 새로 추가된 위치: 각 항목에 자동 선택
 * - 제거된 위치: 각 항목에서 자동 해제
 * - 항목별 수동 해제는 유지 (앞 위치 선택이 바뀌지 않는 한)
 */
export function syncSafetyPositionsFromPreRestoration(
  selectedPositions = [],
  prevSelectedPositions = [],
  { setValue, getValues, isInitial = false } = {}
) {
  if (!setValue || !getValues) return;

  const added = selectedPositions.filter(
    (code) => !prevSelectedPositions.includes(code)
  );
  const removed = prevSelectedPositions.filter(
    (code) => !selectedPositions.includes(code)
  );

  SAFETY_INSPECTION_POSITION_PATHS.forEach((suffix) => {
    const path = getWheelInspectionFieldPath(suffix);
    const current = getValues(path) || [];
    let next;

    if (isInitial) {
      next = current.length > 0 ? current : [...selectedPositions];
    } else {
      if (!added.length && !removed.length) return;
      next = current.filter((code) => !removed.includes(code));
      added.forEach((code) => {
        if (!next.includes(code)) next.push(code);
      });
    }

    next = sortWheelPositionCodes(next);
    if (!_.isEqual(current, next)) {
      setValue(path, next, { shouldDirty: !isInitial });
    }
  });
}

export function getWheelInspectionFieldPath(suffix) {
  return `${inspectionPath}.${suffix}`;
}

export function applyLocationDetailDefaultsForPosition(
  setValue,
  positionCode,
  getValues
) {
  if (!setValue || !positionCode) return;
  Object.entries(DEFAULT_LOCATION_DETAIL).forEach(([key, value]) => {
    if (WHEEL_LOCATION_MANUAL_ONLY_KEYS.includes(key)) return;
    const path = getWheelInspectionFieldPath(`locationDetail.${positionCode}.${key}`);
    const current = getValues?.(path);
    if (current === undefined || current === null || current === '') {
      setValue(path, value, { shouldDirty: false });
    }
  });
}

export function parseWheelInspection(vehicleInformation = {}) {
  const raw = _.get(vehicleInformation, 'wheelInspection');
  const parsed = _.merge({}, DEFAULT_WHEEL_INSPECTION, raw || {});
  parsed.preRestoration.insuranceHandled = normalizeInsuranceHandled(
    _.get(parsed, 'preRestoration.insuranceHandled')
  );
  migrateCuttingPartAndRoadForce(parsed);
  WHEEL_POSITIONS.forEach(({ code }) => {
    parsed.locationDetail[code] = normalizeLocationDetail(
      _.get(parsed, `locationDetail.${code}`)
    );
  });
  return parsed;
}

export function mergeWheelInspectionIntoVehicleInformation(
  vehicleInformation = {},
  wheelInspection
) {
  return {
    ...vehicleInformation,
    wheelInspection: _.merge(
      {},
      DEFAULT_WHEEL_INSPECTION,
      wheelInspection || _.get(vehicleInformation, 'wheelInspection')
    ),
  };
}

export function mergeVehicleWheelData(
  vehicleInformation = {},
  { wheelInformation, wheelInspection } = {}
) {
  return mergeWheelInspectionIntoVehicleInformation(
    mergeWheelIntoVehicleInformation(vehicleInformation, wheelInformation),
    wheelInspection
  );
}

export function getActiveWheelPhotos(fileItems = [], partCode) {
  return fileItems.filter(
    (item) =>
      item.partCode === partCode && item.state !== FileState.Deleted
  );
}

export function countActiveWheelPhotos(fileItems, partCode) {
  return getActiveWheelPhotos(fileItems, partCode).length;
}

/**
 * 작성완료 시 검증. 실패 시 { ok: false, message }.
 */
export function validateWheelInspectionComplete(values, fileItems = []) {
  const inspection = parseWheelInspection(values?.vehicleInformation);
  const insurance = normalizeInsuranceHandled(
    _.get(inspection, 'preRestoration.insuranceHandled')
  );

  if (!insurance.length) {
    return { ok: false, message: '보험처리 여부를 선택해 주세요.' };
  }

  if (insurance.includes('Y')) {
    const plateCount = countActiveWheelPhotos(
      fileItems,
      WHEEL_PHOTO_PART.PRE_PLATE
    );
    if (plateCount < 1) {
      return {
        ok: false,
        message: '보험처리 시 차량번호(번호판) 사진을 등록해 주세요.',
      };
    }
  }

  const positions = _.get(inspection, 'preRestoration.positions') || [];
  for (const pos of positions) {
    const count = countActiveWheelPhotos(fileItems, prePhotoPart(pos));
    if (count > WHEEL_PRE_PHOTOS_PER_POSITION) {
      return {
        ok: false,
        message: `${getPositionLabel(pos)} 복원 전 사진은 1장만 등록할 수 있습니다.`,
      };
    }
  }

  return { ok: true };
}

export function getPositionLabel(code) {
  const labels = { FL: '운전석 앞', FR: '조수석 앞', RL: '운전석 뒤', RR: '조수석 뒤' };
  return labels[code] || code;
}

export function getWheelPositionGroupLabel(code) {
  const labels = {
    FL: '앞바퀴 운전석',
    FR: '앞바퀴 조수석',
    RL: '뒷바퀴 운전석',
    RR: '뒷바퀴 조수석',
  };
  return labels[code] || getPositionLabel(code);
}

export function getPreRestorationPhotoPartCodes(inspection) {
  const insurance = normalizeInsuranceHandled(
    _.get(inspection, 'preRestoration.insuranceHandled')
  );
  const positions = _.get(inspection, 'preRestoration.positions') || [];
  const partCodes = [];

  if (insurance.includes('Y')) {
    partCodes.push(WHEEL_PHOTO_PART.PRE_PLATE);
  }

  WHEEL_POSITIONS.forEach(({ code }) => {
    if (positions.includes(code)) {
      partCodes.push(prePhotoPart(code));
    }
  });

  return partCodes;
}

/** 휠 기록부 앞쪽에 이미 표시되거나 별도 업로드 UI가 없는 사진 */
export function isWheelPreRestorationPhotoPartCode(partCode) {
  return String(partCode || '').startsWith('WHEEL_PRE_');
}

export function isWheelSafetyCrackPhotoPartCode(partCode) {
  return String(partCode || '').startsWith('WHEEL_SAFETY_');
}

export function isWheelLocationPhotoPartCode(partCode) {
  return String(partCode || '').startsWith('WHEEL_LOC_');
}

export function isWheelPhotoExcludedFromAppendix(partCode) {
  return (
    isWheelPreRestorationPhotoPartCode(partCode) ||
    isWheelSafetyCrackPhotoPartCode(partCode) ||
    isWheelLocationPhotoPartCode(partCode)
  );
}

export function hasWheelRestorationReport(vehicleInformation = {}, checkData = null) {
  if (hasWheelInformation(parseWheelInformation(vehicleInformation, checkData))) {
    return true;
  }
  const inspection = parseWheelInspection(vehicleInformation);
  if (normalizeInsuranceHandled(_.get(inspection, 'preRestoration.insuranceHandled')).length) {
    return true;
  }
  if (_.get(inspection, 'preRestoration.positions', []).length) return true;

  const safety = _.get(inspection, 'safety', {});
  if (
    safety.wheelTypes?.length ||
    safety.restorationMethods?.length ||
    safety.clearCoatType ||
    safety.precisionMethods?.length
  ) {
    return true;
  }
  if (
    ['crack', 'hairline', 'paintState', 'roadForce', 'scratch', 'specialNote'].some(
      (key) => safety[key]
    ) ||
    [
      'crackPositions',
      'hairlinePositions',
      'paintStatePositions',
      'roadForcePositions',
      'scratchPositions',
    ].some((key) => safety[key]?.length)
  ) {
    return true;
  }

  return ['FL', 'FR', 'RL', 'RR'].some((pos) =>
    Object.values(_.get(inspection, `locationDetail.${pos}`, {})).some((v) => v)
  );
}
